import React, { FC, useCallback, useMemo, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import { CloseDialogFunc } from '../../hooks/useCloseDialog';
import { useTranslation } from 'react-i18next';
import { getLicenseStatus, LicenseStatus } from '../../helpers/licenseStatus';
import { CellContentTooltip } from '../CellContentTooltip/CellContentTooltip';
import { LicenseExpiry } from '../LicenseExpiry/LicenseExpiry';
import { createStatusChipComponent } from '../StatusChip/StatusChip';
import { CustomerProductPlanType } from '../../models/CustomerProductPlanType';
import { TypographyH5 } from '../Typography/Typography';
import { ifError } from '../../helpers/ifError';
import { ELicenseMode, ILicense } from '../../models/ILicense';

interface IProps {
  onClose: CloseDialogFunc;
  onSubmit: (data: { basePackages: number[]; addonPackages: number[] }) => Promise<void>;
  licenses: ILicense[];
  showAddons: boolean;
  basePackagesMultiselect: boolean;
  isLoading: boolean;
}

const StatusRenderer = createStatusChipComponent({
  valid: ['common.licenseStatus.valid', 'lime'],
  expiresSoon: ['common.licenseStatus.expiresSoon', 'orange'],
});

interface IPackageRow {
  id: number;
  license: string;
  type?: CustomerProductPlanType;
  expires: number;
  status: LicenseStatus;
}

const StyledPackageRow = styled(TableRow)(({ theme, selected }) => ({
  cursor: 'pointer',
  backgroundColor: selected ? theme.palette.primary.main : undefined,
  outline: selected ? `1px solid ${theme.palette.primary.main}` : undefined,
  '&:hover': selected
    ? undefined
    : {
        backgroundColor: alpha(theme.palette.action.selected, theme.palette.action.selectedOpacity),
      },
}));

const StyledTable = styled(Table)({
  tableLayout: 'fixed',
});

const StyledIdHeader = styled(TableCell)({
  width: '6.5em',
});

const StyledExpiresHeader = styled(TableCell)({
  width: '15.5em',
});

const BasePackagesTable: FC<{
  rows: Array<IPackageRow>;
  selectedBasePackages: number[];
  onToggleBasePackage: (id: number) => void;
}> = ({ rows, selectedBasePackages, onToggleBasePackage }) => {
  const { t } = useTranslation();

  return (
    <StyledTable>
      <TableHead>
        <TableRow>
          <StyledIdHeader>{t('common.tableHeader.id')}</StyledIdHeader>
          <TableCell>{t('customerUser.packages.licenseHeader')}</TableCell>
          <StyledExpiresHeader>{t('common.tableHeader.expires')}</StyledExpiresHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <StyledPackageRow
            key={row.id}
            onClick={() => onToggleBasePackage(row.id)}
            selected={selectedBasePackages.includes(row.id)}
          >
            <TableCell>{row.id}</TableCell>
            <TableCell>
              <CellContentTooltip noWrap={true}>
                {row.license} {row.status === 'expiresSoon' ? <StatusRenderer value={row.status} /> : null}
              </CellContentTooltip>
            </TableCell>
            <TableCell>{row.type ? <LicenseExpiry type={row.type} expiry={row.expires} /> : null}</TableCell>
          </StyledPackageRow>
        ))}
      </TableBody>
    </StyledTable>
  );
};

const AddonPackagesTable: FC<{
  rows: Array<IPackageRow>;
  selectedAddonPackages: number[];
  onToggleAddonPackage: (id: number) => void;
}> = ({ rows, selectedAddonPackages, onToggleAddonPackage }) => {
  return (
    <div>
      {rows.map((row) => (
        <div key={row.id}>
          <label>
            <Checkbox checked={selectedAddonPackages.includes(row.id)} onChange={() => onToggleAddonPackage(row.id)} />
            {row.license} {row.status === 'expiresSoon' ? <StatusRenderer value={row.status} /> : null}(
            {row.type ? <LicenseExpiry type={row.type} expiry={row.expires} /> : 'error'})
          </label>
        </div>
      ))}
    </div>
  );
};

export const RequestOfflineChooseLicense: FC<IProps> = ({
  onClose,
  onSubmit,
  licenses,
  showAddons,
  basePackagesMultiselect,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [selectedBasePackages, setSelectedBasePackages] = useState<number[]>([]);
  const [selectedAddonPackages, setSelectedAddonPackages] = useState<number[]>([]);

  const onClick = useCallback(() => {
    onSubmit({
      basePackages: selectedBasePackages,
      addonPackages: selectedAddonPackages,
    });
  }, [onSubmit, selectedBasePackages, selectedAddonPackages]);

  const validPackages = useMemo(
    () =>
      licenses.filter((item) => {
        const status = getLicenseStatus(item.suspended, item.expiresAt, item.planType);
        return item.mode === ELicenseMode.Unused && ['expiresSoon', 'valid'].includes(status) && !item.isDiscovery;
      }),
    [licenses]
  );

  const basePackages = useMemo(() => validPackages.filter((item) => !item.isAddon), [validPackages]);

  const addonPackages = useMemo(() => validPackages.filter((item) => item.isAddon), [validPackages]);

  const baseRows = useMemo<IPackageRow[]>(() => {
    return basePackages.map((item) => {
      return {
        id: ifError(() => item.id!, 0),
        license: ifError(() => item.name, ''),
        type: ifError(() => item.planType, undefined),
        expires: ifError(() => item.expiresAt || item.updatesExpiresAt, 0),
        status: ifError(() => getLicenseStatus(item.suspended, item.expiresAt, item.planType), 'expired'),
      };
    });
  }, [basePackages]);

  const addonRows = useMemo<IPackageRow[]>(() => {
    return addonPackages.map((item) => {
      return {
        id: ifError(() => item.id!, 0),
        license: ifError(() => item.name, ''),
        type: ifError(() => item.planType, undefined),
        expires: ifError(() => item.expiresAt || item.updatesExpiresAt, 0),
        status: ifError(() => getLicenseStatus(item.suspended, item.expiresAt, item.planType), 'expired'),
      };
    });
  }, [addonPackages]);

  const toggleAddonPackage = useCallback(
    (id: number) => {
      if (selectedAddonPackages.includes(id)) {
        setSelectedAddonPackages(selectedAddonPackages.filter((packageId) => packageId !== id));
      } else {
        setSelectedAddonPackages([...selectedAddonPackages, id]);
      }
    },
    [selectedAddonPackages, addonPackages]
  );

  const toggleBasePackage = useCallback(
    (id: number) => {
      if (basePackagesMultiselect) {
        if (selectedBasePackages.includes(id)) {
          setSelectedBasePackages(selectedBasePackages.filter((packageId) => packageId !== id));
        } else {
          setSelectedBasePackages([...selectedBasePackages, id]);
        }
      } else {
        setSelectedBasePackages([id]);
      }
    },
    [selectedBasePackages, basePackages, basePackagesMultiselect]
  );

  return (
    <>
      <DialogTitle>{t('licenses.offlineDialog.title')}</DialogTitle>
      <DialogContent>
        {baseRows.length === 0 ? (
          isLoading ? (
            t('component.requestOffline.loading')
          ) : (
            t('component.requestOffline.noAvailableLicenses')
          )
        ) : (
          <>
            <Box p={1}>
              <TypographyH5>{t('component.requestOffline.selectPackage')}</TypographyH5>
            </Box>
            <BasePackagesTable
              rows={baseRows}
              selectedBasePackages={selectedBasePackages}
              onToggleBasePackage={toggleBasePackage}
            />
          </>
        )}

        {/* Don't show addons if there are no base licenses available */}
        {showAddons && baseRows.length > 0 && addonRows.length > 0 ? (
          <>
            <Box p={1}>
              <TypographyH5>{t('component.requestOffline.chooseAddons')}</TypographyH5>
            </Box>
            <AddonPackagesTable
              rows={addonRows}
              selectedAddonPackages={selectedAddonPackages}
              onToggleAddonPackage={toggleAddonPackage}
            />
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={isLoading}>
          {t('common.dialog.close')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={selectedBasePackages.length === 0 || isLoading}
          onClick={onClick}
          endIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {t('component.requestOffline.confirm')}
        </Button>
      </DialogActions>
    </>
  );
};
